import React from "react";

import { Box, Collapse, Typography, alpha, useTheme } from "@mui/material";

import type { TaskStep } from "./task-steps-helpers";

/**
 * Expandable step details with inline log output.
 */
export function TaskStepDetails({
  step,
  isExpanded,
}: {
  step: TaskStep;
  isExpanded: boolean;
}): React.ReactElement {
  const theme = useTheme();
  const hasLogs = step.logLines && step.logLines.length > 0;
  return (
    <Collapse in={isExpanded} timeout={200}>
      <Box
        sx={{
          maxHeight: 300,
          overflow: "auto",
          bgcolor: alpha("#000000", 0.03),
          px: 2,
          py: 1,
          fontFamily: "monospace",
          fontSize: "0.7rem",
          lineHeight: 1.6,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.04)}`,
        }}
      >
        {hasLogs ? (
          step.logLines!.map((line, i) => (
            <Typography
              key={i}
              variant="inherit"
              component="div"
              sx={{
                color:
                  line.toLowerCase().includes("error") ||
                  line.toLowerCase().includes("failed")
                    ? "error.main"
                    : line.toLowerCase().includes("warn")
                      ? "warning.main"
                      : "text.secondary",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
              }}
            >
              {line}
            </Typography>
          ))
        ) : (
          <Typography
            variant="caption"
            sx={{
              color: step.htmlUrl ? "primary.main" : "text.secondary",
            }}
          >
            {step.status === "in_progress" ? (
              "Running..."
            ) : step.status === "pending" || step.status === "queued" ? (
              "Waiting to start..."
            ) : step.htmlUrl ? (
              <a
                href={step.htmlUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "inherit",
                  textDecoration: "underline",
                }}
              >
                View full log on GitHub →
              </a>
            ) : (
              "No log output"
            )}
          </Typography>
        )}
      </Box>
    </Collapse>
  );
}
